import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, SegmentedControl, SidePanel, Table, useToast } from '../ui';
import type { TableColumn } from '../ui';
import { useUser } from '../../context/UserContext';
import { useUrlParam } from '../../hooks/useUrlParam';
import { timeAgo } from '../../lib/comms';
import { AI_JOB_STATUS_LABEL, aiJobStatusTone } from '../../lib/aiJobLabels';
import { uniqueChannelName } from '../../lib/realtime';
import { supabase } from '../../lib/supabase';
import {
  deleteCascadeCall,
  enqueueSecurityAudit,
  fetchAuditJobs,
  fetchCascadeCalls,
  fetchSystems,
  isOpenAudit,
  runningLabel,
  setSystemNotes,
  siteUrl,
  systemSites,
  updateCascadeCall,
  type AuditJob,
  type CascadeCall,
  type SecuritySystem,
} from '../../lib/securityAudits';

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
const linkStyle: React.CSSProperties = { color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' };

/** Nothing to audit: a database we can't see that no repo points at. */
const unauditable = (s: SecuritySystem) => s.kind === 'database' && !s.reachable && !s.repos.length;

/**
 * Security → Audits: every system, database ones first, each with its last audit and an Audit button. One audit runs
 * per system (the database refuses a second open security_audit job for the same system), so that row's button says Running.
 * A row opens its panel (?system=): facts, notes, audit history and the saved cascade calls.
 */
export default function AuditsView({ search, onCount }: { search: string; onCount: (n: number) => void }) {
  const [systems, setSystems] = useState<SecuritySystem[]>([]);
  const [jobs, setJobs] = useState<AuditJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  const [openId, setOpenId] = useUrlParam('system');
  // ?sort=oldest: built longest ago first. Kyle (2026-10-25): the oldest builds came from the weakest models.
  const [sortParam, setSortParam] = useUrlParam('sort');
  const sort: SystemSort = sortParam === 'oldest' ? 'oldest' : 'name';
  const { user } = useUser();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [s, j] = await Promise.all([fetchSystems(), fetchAuditJobs()]);
      setSystems(s);
      setJobs(j);
    } catch (err) {
      console.error('Error loading security systems:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load the systems');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // An audit moving through the queue changes the button and the last-audit column; follow it live.
  useEffect(() => {
    const channel = supabase
      .channel(uniqueChannelName('security_audit_jobs'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_jobs', filter: 'task_type=eq.security_audit' }, () => {
        fetchAuditJobs().then(setJobs).catch(() => {});
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (!loading) onCount(systems.length);
  }, [systems.length, loading, onCount]);

  // One open audit per system; different systems run side by side.
  const openBySystem = useMemo(() => {
    const m = new Map<string, AuditJob>();
    for (const j of jobs) if (j.security_system_id && isOpenAudit(j.status) && !m.has(j.security_system_id)) m.set(j.security_system_id, j);
    return m;
  }, [jobs]);
  const openJobs = useMemo(() => [...openBySystem.values()], [openBySystem]);
  const lastJob = useMemo(() => {
    const m = new Map<string, AuditJob>();
    for (const j of jobs) if (j.security_system_id && !m.has(j.security_system_id)) m.set(j.security_system_id, j);
    return m;
  }, [jobs]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return systems
      .filter(
        (s) =>
          !q ||
          [s.name, s.client_name, s.supabase_ref, s.key, ...s.repos, ...systemSites(s).flatMap((x) => [x.name, ...(x.domains ?? [])])].some((v) => v?.toLowerCase().includes(q)),
      )
      .sort(sort === 'oldest' ? byOldest : (a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'database' ? -1 : 1));
  }, [systems, search, sort]);

  const open = systems.find((s) => s.id === openId) ?? null;

  const start = async (s: SecuritySystem) => {
    if (!user) return;
    setConfirmId(null);
    setStarting(s.id);
    try {
      const job = await enqueueSecurityAudit(s, { id: user.id, name: user.name });
      setJobs((prev) => [job as unknown as AuditJob, ...prev]);
      toast.success(`Audit queued for ${s.name}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start the audit');
      fetchAuditJobs().then(setJobs).catch(() => {});
    } finally {
      setStarting(null);
    }
  };

  const auditControl = (s: SecuritySystem) => {
    if (confirmId === s.id) {
      return (
        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
          <Button size="sm" variant="primary" onClick={() => start(s)}>
            Start audit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>
            Cancel
          </Button>
        </span>
      );
    }
    const open = openBySystem.get(s.id);
    const blocked = open ? `${s.name} already has an audit open. Finish or cancel it first.` : unauditable(s) ? 'No access to this database and no repo points at it.' : null;
    return (
      <span onClick={(e) => e.stopPropagation()} title={blocked ?? `Queue a Security Audit of ${s.name}`} style={{ display: 'inline-flex' }}>
        <Button size="sm" variant="secondary" icon="shield-check" disabled={Boolean(blocked) || starting !== null} onClick={() => setConfirmId(s.id)}>
          {starting === s.id ? 'Queuing…' : open ? 'Running' : 'Audit'}
        </Button>
      </span>
    );
  };

  const columns: TableColumn<SecuritySystem>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'System',
        width: '24%',
        render: (_, s) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            <span style={{ fontWeight: 500, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
            <span style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              <KindBadge system={s} />
              {s.client_name ? <Badge tone="accent">{s.client_name}</Badge> : null}
            </span>
            <BuiltLine system={s} />
          </div>
        ),
      },
      { key: 'repos', label: 'Repos and sites', wrap: true, render: (_, s) => <RepoSiteLinks system={s} /> },
      { key: 'supabase_ref', label: 'Database', width: '17%', render: (_, s) => <DatabaseCell system={s} /> },
      { key: 'last', label: 'Last audit', width: '15%', render: (_, s) => <LastAudit job={lastJob.get(s.id)} /> },
      { key: 'audit', label: '', width: '230px', align: 'right', render: (_, s) => auditControl(s) },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastJob, openBySystem, confirmId, starting],
  );

  return (
    <>
      {openJobs.map((job) => (
        <div
          key={job.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            padding: '10px 14px',
            marginBottom: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--accent-soft)',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-body)',
          }}
        >
          <Badge tone={aiJobStatusTone(job.status)} dot pulse>
            {AI_JOB_STATUS_LABEL[job.status]}
          </Badge>
          <span>
            Auditing <strong style={{ fontWeight: 600 }}>{runningLabel(job, systems)}</strong>.
          </span>
          <Link to={`/agents?job=${job.id}`} style={{ color: 'var(--accent-text)', marginLeft: 'auto' }}>
            Open the job →
          </Link>
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3) var(--space-5)', flexWrap: 'wrap', margin: '0 0 var(--space-5)' }}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', margin: 0, flex: '1 1 420px' }}>
          A system is one database plus every repo and site that uses it, or a repo with no database. Start as many as you like, one open audit per system; each finding comes back for your Fix, Skip or
          Later on the job page.
        </p>
        <SegmentedControl
          options={[
            { id: 'name', label: 'A–Z' },
            { id: 'oldest', label: 'Oldest first' },
          ]}
          value={sort}
          onChange={(id) => setSortParam(id === 'oldest' ? 'oldest' : null)}
          style={{ flexShrink: 0 }}
        />
      </div>

      {loadError ? (
        <Card>
          <div style={{ color: 'var(--status-down)', fontSize: 'var(--text-sm)' }}>{loadError}</div>
        </Card>
      ) : loading ? null : visible.length === 0 ? (
        <Card>
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
            {systems.length === 0 ? 'No systems yet. Run scripts/infra-inventory.mjs systems.' : 'Nothing matches.'}
          </div>
        </Card>
      ) : (
        <>
          {/* Phones: a card per system. */}
          <div className="md:hidden" style={{ background: 'var(--surface-card)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            {visible.map((s, i) => (
              <div
                key={s.id}
                onClick={() => setOpenId(s.id)}
                style={{ padding: '14px', borderTop: i === 0 ? 'none' : '1px solid var(--border-hairline)', display: 'flex', flexDirection: 'column', gap: '8px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 500, color: 'var(--text-body)', wordBreak: 'break-word' }}>{s.name}</span>
                  <KindBadge system={s} />
                </div>
                <BuiltLine system={s} />
                <DatabaseCell system={s} />
                <LastAudit job={lastJob.get(s.id)} />
                <div>{auditControl(s)}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block" style={{ overflowX: 'auto' }}>
            <Table columns={columns} rows={visible} onRowClick={(s) => setOpenId(s.id)} style={{ minWidth: '1000px' }} />
          </div>
        </>
      )}

      <SidePanel
        open={Boolean(open)}
        onClose={() => setOpenId(null)}
        eyebrow={open ? (open.kind === 'database' ? 'Database system' : 'Static system') : ''}
        title={open?.name ?? ''}
        width={560}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '8px' }}>
            {open ? auditControl(open) : <span />}
            <Button variant="ghost" onClick={() => setOpenId(null)}>
              Close
            </Button>
          </div>
        }
      >
        {open ? (
          <SystemPanel
            key={open.id}
            system={open}
            jobs={jobs.filter((j) => j.security_system_id === open.id)}
            userId={user?.id ?? null}
            onNotes={(notes) => setSystems((prev) => prev.map((s) => (s.id === open.id ? { ...s, notes } : s)))}
          />
        ) : null}
      </SidePanel>
    </>
  );
}

function KindBadge({ system: s }: { system: SecuritySystem }) {
  if (s.kind === 'static') return <Badge tone="neutral">Static</Badge>;
  if (!s.repos.length && !systemSites(s).length) return <Badge tone="warn">Database · nothing uses it</Badge>;
  return <Badge tone="info">Database</Badge>;
}

type SystemSort = 'name' | 'oldest';

/** Oldest build first; systems with no known build date go last, by name. */
function byOldest(a: SecuritySystem, b: SecuritySystem) {
  const ta = a.built_at ? Date.parse(a.built_at) : Infinity;
  const tb = b.built_at ? Date.parse(b.built_at) : Infinity;
  return ta === tb ? a.name.localeCompare(b.name) : ta - tb;
}

/** When the system was first built: the earliest of its database's and its repos' creation dates. */
function BuiltLine({ system: s }: { system: SecuritySystem }) {
  if (!s.built_at) return null;
  const d = new Date(s.built_at);
  return (
    <span className="t-num" style={{ ...faint, fontSize: 'var(--text-xs)' }} title={`Built ${d.toLocaleDateString()}: the earliest of its database's and repos' creation dates`}>
      Built {d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
    </span>
  );
}

function DatabaseCell({ system: s, org = false }: { system: SecuritySystem; org?: boolean }) {
  if (s.kind === 'static' || !s.supabase_ref) return <span style={{ ...faint, fontSize: 'var(--text-sm)' }}>No database</span>;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
      <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {s.supabase_ref}
      </span>
      {s.reachable ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-up)' }}>Reachable{org && s.supabase_org ? ` · org ${s.supabase_org}` : ''}</span>
      ) : (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-down)' }}>Can't audit, no access</span>
      )}
    </span>
  );
}

/** The table shows a few of each; the panel lists them all. */
const ROW_REPOS = 4;
const ROW_SITES = 3;

function RepoSiteLinks({ system: s }: { system: SecuritySystem }) {
  const allSites = systemSites(s);
  const sites = allSites.slice(0, ROW_SITES);
  const repos = s.repos.slice(0, ROW_REPOS);
  const more = s.repos.length - repos.length + (allSites.length - sites.length);
  if (!repos.length && !sites.length) return <span style={{ ...faint, fontSize: 'var(--text-sm)' }}>No repo or site points at it</span>;
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: 'var(--text-sm)', minWidth: 0 }}>
      {repos.length ? (
        <span style={{ display: 'flex', gap: '4px 10px', flexWrap: 'wrap' }}>
          {repos.map((r) => (
            <a key={r} href={`https://github.com/${r}`} target="_blank" rel="noopener noreferrer" onClick={stop} style={linkStyle}>
              {r.replace(/^eczzz\//, '')}
            </a>
          ))}
        </span>
      ) : null}
      {sites.length ? (
        <span style={{ display: 'flex', gap: '4px 10px', flexWrap: 'wrap' }}>
          {sites.map((x) => {
            const url = siteUrl(x);
            return url ? (
              <a key={x.id} href={url} target="_blank" rel="noopener noreferrer" onClick={stop} style={{ ...linkStyle, color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
                {url.replace(/^https?:\/\//, '')} ↗
              </a>
            ) : (
              <span key={x.id} style={{ ...faint, fontSize: 'var(--text-xs)' }}>
                {x.name}
              </span>
            );
          })}
        </span>
      ) : null}
      {more > 0 ? (
        <span style={{ ...faint, fontSize: 'var(--text-xs)' }}>
          +{more} more ({s.repos.length} repos, {allSites.length} sites)
        </span>
      ) : null}
    </div>
  );
}

function LastAudit({ job }: { job?: AuditJob }) {
  if (!job) return <span style={{ ...faint, fontSize: 'var(--text-sm)' }}>Never audited</span>;
  const findings = job.operator_summary?.spec?.findings?.length;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' }}>
      <Link to={`/agents?job=${job.id}`} onClick={(e) => e.stopPropagation()} style={{ textDecoration: 'none' }}>
        <Badge tone={aiJobStatusTone(job.status)} dot>
          {AI_JOB_STATUS_LABEL[job.status]}
        </Badge>
      </Link>
      <span className="t-num" style={{ ...faint, fontSize: 'var(--text-xs)' }}>
        {timeAgo(job.updated_at)}
        {findings != null ? ` · ${findings} ${findings === 1 ? 'finding' : 'findings'}` : ''}
      </span>
    </span>
  );
}

function Section({ title, children, actions }: { title: string; children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px', gap: '8px' }}>
        <div className="t-eyebrow">{title}</div>
        {actions}
      </div>
      {children}
    </div>
  );
}

function SystemPanel({ system: s, jobs, userId, onNotes }: { system: SecuritySystem; jobs: AuditJob[]; userId: string | null; onNotes: (notes: string | null) => void }) {
  const [notes, setNotes] = useState(s.notes ?? '');
  const [calls, setCalls] = useState<CascadeCall[] | null>(null);
  const [callsError, setCallsError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    fetchCascadeCalls(s.id)
      .then(setCalls)
      .catch((err) => setCallsError(err instanceof Error ? err.message : 'Could not load the cascade calls'));
  }, [s.id]);

  const saveNotes = async () => {
    const value = notes.trim() || null;
    if (value === (s.notes ?? null)) return;
    try {
      await setSystemNotes(s.id, value);
      onNotes(value);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the note');
    }
  };

  const sites = systemSites(s);
  const facts: Array<[string, React.ReactNode]> = [
    ['Key', <span className="t-num">{s.key}</span>],
    ['Database', s.supabase_ref ? <DatabaseCell system={s} org /> : 'None: codebase and live config checks only'],
  ];
  if (s.client_name) facts.push(['Client', s.client_name]);
  facts.push(['Seen', timeAgo(s.seen_at)]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', fontSize: 'var(--text-sm)' }}>
      <Section title="Facts">
        <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '6px 12px' }}>
          {facts.map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <span style={faint}>{k}</span>
              <span style={{ color: 'var(--text-body)', wordBreak: 'break-word' }}>{v}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Repos · ${s.repos.length}`}>
        {s.repos.length ? (
          s.repos.map((r) => (
            <div key={r} style={{ padding: '3px 0' }}>
              <a href={`https://github.com/${r}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-body)' }}>
                {r}
              </a>
            </div>
          ))
        ) : (
          <span style={faint}>No repo points at this database.</span>
        )}
      </Section>

      {sites.length ? (
        <Section title={`Netlify sites · ${sites.length}`}>
          {sites.map((x) => {
            const url = siteUrl(x);
            return (
              <div key={x.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '3px 0' }}>
                <a href={`https://app.netlify.com/sites/${x.name}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-body)' }}>
                  {x.name}
                </a>
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-muted)' }}>
                    {url.replace(/^https?:\/\//, '')} ↗
                  </a>
                ) : null}
              </div>
            );
          })}
        </Section>
      ) : null}

      <Section title="Notes">
        <textarea
          className="ck-textarea"
          rows={2}
          placeholder="Anything the next audit should know (it rides in the brief)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={saveNotes}
          style={{ width: '100%' }}
        />
      </Section>

      <Section title={`Audits · ${jobs.length}`}>
        {jobs.length ? (
          jobs.map((j) => {
            const f = j.operator_summary?.spec?.findings;
            return (
              <Link
                key={j.id}
                to={`/agents?job=${j.id}`}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '6px 0', borderTop: '1px solid var(--border-hairline)', textDecoration: 'none' }}
              >
                <span style={{ display: 'flex', gap: '8px', alignItems: 'center', minWidth: 0 }}>
                  <Badge tone={aiJobStatusTone(j.status)} dot>
                    {AI_JOB_STATUS_LABEL[j.status]}
                  </Badge>
                  <span style={{ color: 'var(--text-muted)' }}>{f ? `${f.length} ${f.length === 1 ? 'finding' : 'findings'}` : 'No report yet'}</span>
                </span>
                <span className="t-num" style={faint}>
                  {new Date(j.created_at).toLocaleDateString()} ({timeAgo(j.created_at)})
                </span>
              </Link>
            );
          })
        ) : (
          <span style={faint}>Never audited.</span>
        )}
      </Section>

      <Section title={`Saved cascade calls${calls ? ` · ${calls.length}` : ''}`}>
        <p style={{ margin: '0 0 8px', color: 'var(--text-muted)' }}>
          Your call on each cascading foreign key. Answering a cascade finding saves it here; the next audit reads these and never asks about them again.
        </p>
        {callsError ? (
          <span style={{ color: 'var(--status-down)' }}>{callsError}</span>
        ) : calls === null ? null : calls.length === 0 ? (
          <span style={faint}>None saved yet.</span>
        ) : (
          calls.map((c) => (
            <CascadeCallRow
              key={c.id}
              call={c}
              userId={userId}
              onChange={(next) => setCalls((prev) => (prev ?? []).map((x) => (x.id === c.id ? next : x)))}
              onDelete={() => setCalls((prev) => (prev ?? []).filter((x) => x.id !== c.id))}
            />
          ))
        )}
      </Section>
    </div>
  );
}

function CascadeCallRow({ call: c, userId, onChange, onDelete }: { call: CascadeCall; userId: string | null; onChange: (c: CascadeCall) => void; onDelete: () => void }) {
  const [note, setNote] = useState(c.note ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const save = async (patch: { call?: 'good' | 'bad'; note?: string | null }) => {
    setBusy(true);
    try {
      await updateCascadeCall(c.id, patch, userId);
      onChange({ ...c, ...patch, decided_at: new Date().toISOString() });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the call');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteCascadeCall(c.id);
      onDelete();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the call');
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--border-hairline)', display: 'flex', flexDirection: 'column', gap: '8px', opacity: busy ? 0.6 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ minWidth: 0 }}>
          <span className="t-num" style={{ color: 'var(--text-body)' }}>
            {c.from_table} → {c.to_table}
          </span>
          <span className="t-num" style={{ ...faint, fontSize: 'var(--text-xs)', display: 'block', wordBreak: 'break-all' }}>
            {c.constraint_name}
          </span>
        </span>
        <SegmentedControl
          options={[
            { id: 'good', label: 'Good cascade' },
            { id: 'bad', label: 'Change it' },
          ]}
          value={c.call}
          onChange={(id) => id !== c.call && save({ call: id as 'good' | 'bad' })}
        />
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          className="ck-textarea"
          style={{ height: '28px', minHeight: 0, padding: '0 10px', flex: 1, fontSize: 'var(--text-sm)', resize: 'none' }}
          placeholder="Why (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => (note.trim() || null) !== (c.note ?? null) && save({ note: note.trim() || null })}
        />
        {confirmDelete ? (
          <span style={{ display: 'inline-flex', gap: '4px' }}>
            <Button size="sm" variant="danger" onClick={remove}>
              Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
              Keep
            </Button>
          </span>
        ) : (
          <Button size="sm" variant="ghost" icon="trash-2" onClick={() => setConfirmDelete(true)} title="Forget this call; the next audit asks again">
            Forget
          </Button>
        )}
      </div>
      <span className="t-num" style={{ ...faint, fontSize: 'var(--text-xs)' }}>
        Decided {timeAgo(c.decided_at)}
      </span>
    </div>
  );
}
